import _ from 'lodash'
import {MutationTree} from 'vuex'
import {SEOState} from '@/modules/seo/store'
import {Metadata, MetadataTranslation, SEOCompletor, TouchedFields, AnalysisReport} from '@/modules/seo/types'

export enum MutationTypes {
    SetCurrentMetadata = 'metadata/current/set',
    SetCurrentTranslation = 'metadata/current/translation/set',
    UpdateCurrentTranslationField = 'metadata/current/translation/field/update',
    SetSEOCompletor = 'completor/set',
    AddMetadataTouchedField = 'metadata/field/touched/add',
    SetMetadataTouchedFields = 'metadata/fields/touched/set',
    SetCurrentSEOReport = 'analysis/report/current/set',
    SetAnalysisProcessing = 'analysis/processing/set',
}

const getCurrentTranslation = (state: SEOState) => {
    return state.currentMetadata.translations.find(t => t.locale === state.currentLocale)
}

const addMetadataTouchedField = (state: SEOState, locale: string, field: string) => {
    const translation = state.touchedMetadataFields.get(locale) || []
    const index = translation.findIndex(f => f === field)
    if (index !== -1) {
        return
    }

    translation.push(field)
    state.touchedMetadataFields.set(locale, translation)
}

const deleteMetadataTouchedField = (state: SEOState, locale: string, field: string) => {
    const translation = state.touchedMetadataFields.get(locale) || []
    const index = translation.findIndex(f => f === field)
    if (index === -1) {
        return
    }

    translation.splice(index, 1)
    state.touchedMetadataFields.set(locale, translation)
}

const mutations: MutationTree<SEOState> = {

    /**
     * Establece la información SEO actualmente en edición
     *
     * @param state
     * @param {Metadata} current
     * @returns {Metadata}
     */
    [MutationTypes.SetCurrentMetadata]: (state, current: Metadata) => state.currentMetadata = current,

    /**
     * Establece que traducción de los meta-información actualmente en edición se mostrará
     * al usuario
     *
     * @param state
     * @param {string} current
     * @returns {string}
     */
    [MutationTypes.SetCurrentTranslation]: (state, current: string) => {
        state.currentLocale = current
        const currentMetadata = state.currentMetadata

        // Si los metadatos no posee dicha traducción, se creara una nueva
        if (!currentMetadata.hasTranslation(current)) {
            currentMetadata.addTranslation(new MetadataTranslation(current))
        }
    },

    /**
     * Actualiza un campo del objeto de metadatos SEO que se encuentra actualmente
     * en edición
     *
     * @param state
     * @param {{field: string; value: any}} payload
     */
    [MutationTypes.UpdateCurrentTranslationField]: (state, payload: { field: string, value: any }) => {
        const translation = getCurrentTranslation(state)
        if (!translation) {
            return
        }

        state.currentMetadata.addTranslation(_.set(translation, payload.field, payload.value))

        // Si el campo esta vacio, se elimina de los campos tocados por el usuario
        if (_.isEmpty(payload.value)) {
            deleteMetadataTouchedField(state, translation.locale, payload.field)

            return
        }

        addMetadataTouchedField(state, translation.locale, payload.field)
    },

    /**
     * Registra el componente encargado de auto-completar el SEO
     */
    [MutationTypes.SetSEOCompletor]: (state, payload: SEOCompletor) => state.seoCompletor = payload,

    /**
     * Registra un campo de la metadata como modificado por el usuario
     */
    [MutationTypes.AddMetadataTouchedField]: (state, payload: {locale: string, fieldName: string}) => {
        addMetadataTouchedField(state, payload.locale, payload.fieldName)
    },

    /**
     * Establece la lista de campos SEO modificados por el usuario
     */
    [MutationTypes.SetMetadataTouchedFields]: (state, fields: Map<string, string[]>) => {
        state.touchedMetadataFields = fields
    },

    /**
     * Establece el reporte de análisis SEO mostrado al usuario
     */
    [MutationTypes.SetCurrentSEOReport]: (state, report: AnalysisReport) => {
        state.currentAnalysisReport = report
    },

    /**
     * Establece si el análisis SEO se encuentra en ejecución
     */
    [MutationTypes.SetAnalysisProcessing]: (state, processing: boolean) => {
        state.processingSEOAnalysis = processing
    },
}

export default mutations
